import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Download, Sparkles, Loader2, Check, X, Pencil, Lock, Save, Library } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../components/RichTextEditor";
import DocumentsPanel from "../components/DocumentsPanel";
const PRICE_SAVE_DEBOUNCE_MS = 700;
const NOTES_SAVE_DEBOUNCE_MS = 900;

export default function AssessmentPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const saveTimer = useRef(null);
  const notesTimer = useRef(null);

  useEffect(() => {
    api.get(`/interviews/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Not found"));
  }, [id]);

  const latestNotesRef = useRef(null); // holds the most recent unsaved HTML for beforeunload flush

  const updateNotes = (html) => {
    setData((prev) => prev ? { ...prev, consultant_notes: html } : prev);
    latestNotesRef.current = html;
    if (notesTimer.current) clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      setNotesSaving(true);
      try {
        await api.put(`/interviews/${id}`, { consultant_notes: html });
        latestNotesRef.current = null; // saved
      } catch {
        toast.error("Couldn't save notes");
      } finally {
        setNotesSaving(false);
      }
    }, NOTES_SAVE_DEBOUNCE_MS);
  };

  // Flush any pending notes save on unmount/navigation so the debounce timer
  // can't lose the last few keystrokes when the user clicks Regenerate, navigates,
  // or closes the tab.
  useEffect(() => {
    const beforeUnload = () => {
      const html = latestNotesRef.current;
      if (!html) return;
      const token = localStorage.getItem("gl_token") || "";
      const url = `${api.defaults.baseURL || ""}/interviews/${id}`;
      const blob = new Blob(
        [JSON.stringify({ consultant_notes: html })],
        { type: "application/json" },
      );
      // sendBeacon is fire-and-forget and survives page unload; we can't set Authorization
      // header on it, so instead include the token as a query param that the backend
      // interprets via a fallback. Since our auth middleware only reads Authorization,
      // we fall back to a synchronous XHR (deprecated but supported) as belt-and-braces.
      try {
        navigator.sendBeacon?.(url, blob);
      } catch {}
      try {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url, false); // synchronous — must complete before unload
        xhr.setRequestHeader("Content-Type", "application/json");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.send(JSON.stringify({ consultant_notes: html }));
      } catch {}
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      // Also flush on component unmount (React Router navigation) — fire the pending timer
      // immediately if there's unsaved content.
      if (notesTimer.current) {
        clearTimeout(notesTimer.current);
        const html = latestNotesRef.current;
        if (html != null) {
          api.put(`/interviews/${id}`, { consultant_notes: html }).catch(() => {});
        }
      }
    };
  }, [id]);

  const saveAsSnapshot = async () => {
    if (!data?.assessment) return;
    const defaultName = data.industry
      ? `${data.industry} — ${data.business_name || data.client_name}`
      : (data.business_name || data.client_name || "Untitled snapshot");
    const name = window.prompt("Name this snapshot (e.g. 'HVAC Lead Hub')", defaultName);
    if (!name) return;
    const industry = window.prompt("Industry tag (used to auto-match future clients)", data.industry || "");
    if (industry === null) return;
    const summary = window.prompt("One-line summary (optional, e.g. 'AI lead hub on GHL for residential HVAC')", "");
    try {
      await api.post("/snapshots", {
        name: name.trim(),
        industry: (industry || "").trim(),
        summary: (summary || "").trim(),
        interview_id: id,
      });
      toast.success("Snapshot saved to library");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const persistAssessment = async (newAssessment) => {
    setSaving(true);
    try {
      await api.put(`/interviews/${id}/assessment`, { assessment: newAssessment });
    } catch {
      toast.error("Couldn't save price changes");
    } finally {
      setSaving(false);
    }
  };

  const updateTierPrice = (tierKey, newPrice) => {
    const val = Math.max(0, parseInt(newPrice || 0, 10));
    setData((prev) => {
      const next = {
        ...prev,
        assessment: {
          ...prev.assessment,
          engagement_options: {
            ...prev.assessment.engagement_options,
            [tierKey]: {
              ...prev.assessment.engagement_options[tierKey],
              price: val,
            },
          },
        },
      };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persistAssessment(next.assessment), PRICE_SAVE_DEBOUNCE_MS);
      return next;
    });
  };

  const recomputeFinancials = (assessment) => {
    // Only sum tools selected for the PPT (backward compat: missing flag = selected).
    const toolCost = (assessment.recommended_solutions || [])
      .filter((r) => r.selected_for_ppt !== false)
      .reduce((sum, r) => sum + (parseFloat(r.monthly_cost) || 0), 0);
    const monthlyValue = parseFloat(assessment.financial_impact?.monthly_value || 0);
    return {
      ...assessment,
      financial_impact: {
        ...(assessment.financial_impact || {}),
        monthly_tool_cost: Math.round(toolCost * 100) / 100,
        monthly_net_roi: Math.round((monthlyValue - toolCost) * 100) / 100,
      },
    };
  };

  const PPT_REC_LIMIT = 8;

  const toggleRecSelection = (idx) => {
    setData((prev) => {
      const recs = [...(prev.assessment.recommended_solutions || [])];
      const isOn = recs[idx].selected_for_ppt !== false;
      // Count currently selected (treat missing flag as true)
      const currentlyOn = recs.filter((r) => r.selected_for_ppt !== false).length;
      if (!isOn && currentlyOn >= PPT_REC_LIMIT) {
        toast.error(`You can include at most ${PPT_REC_LIMIT} tools in the PowerPoint. Uncheck another first.`);
        return prev;
      }
      recs[idx] = { ...recs[idx], selected_for_ppt: !isOn };
      const next = recomputeFinancials({ ...prev.assessment, recommended_solutions: recs });
      queueSave(next);
      return { ...prev, assessment: next };
    });
  };

  const queueSave = (nextAssessment) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistAssessment(nextAssessment), PRICE_SAVE_DEBOUNCE_MS);
  };

  const updateRecSolution = async (idx, patch, { lookupPriceFor, regenerateFor } = {}) => {
    // Optimistic update first
    setData((prev) => {
      const recs = [...(prev.assessment.recommended_solutions || [])];
      recs[idx] = { ...recs[idx], ...patch };
      const next = recomputeFinancials({ ...prev.assessment, recommended_solutions: recs });
      queueSave(next);
      return { ...prev, assessment: next };
    });
    // Tool name changed — regenerate card content (why_it_fits, setup, savings, alternatives) via LLM
    if (regenerateFor) {
      try {
        const r = await api.post(`/interviews/${id}/regenerate-recommendation`, {
          rec_idx: idx,
          new_tool_name: regenerateFor,
        });
        const fresh = r.data || {};
        setData((prev) => {
          const recs = [...(prev.assessment.recommended_solutions || [])];
          // Merge alternatives: keep the previously-shown tool that pickTool preserved
          // in state, AND add any new alternatives the LLM suggested. Dedupe and exclude the
          // currently-selected tool. This is what makes "swap back to the original" possible.
          const mergedAlts = [...new Set([
            ...(recs[idx].alternatives || []),
            ...(fresh.alternatives || []),
          ].filter((x) => x && x !== recs[idx].tool_name))];
          recs[idx] = {
            ...recs[idx],
            why_it_fits: fresh.why_it_fits ?? recs[idx].why_it_fits,
            setup_minutes: fresh.setup_minutes ?? recs[idx].setup_minutes,
            monthly_cost: fresh.monthly_cost ?? recs[idx].monthly_cost,
            hours_saved_weekly: fresh.hours_saved_weekly ?? recs[idx].hours_saved_weekly,
            alternatives: mergedAlts,
            build_in_house: fresh.build_in_house ?? recs[idx].build_in_house,
            price_refreshed: fresh.price_refreshed ?? recs[idx].price_refreshed,
            price_source: fresh.price_source ?? recs[idx].price_source,
          };
          const next = recomputeFinancials({ ...prev.assessment, recommended_solutions: recs });
          queueSave(next);
          return { ...prev, assessment: next };
        });
      } catch {
        // Silent — keep old description, user can manually edit
      }
    } else if (lookupPriceFor) {
      try {
        const r = await api.get(`/catalog/lookup?name=${encodeURIComponent(lookupPriceFor)}`);
        if (r.data?.price > 0) {
          setData((prev) => {
            const recs = [...(prev.assessment.recommended_solutions || [])];
            recs[idx] = { ...recs[idx], monthly_cost: r.data.price, price_refreshed: true };
            const next = recomputeFinancials({ ...prev.assessment, recommended_solutions: recs });
            queueSave(next);
            return { ...prev, assessment: next };
          });
        }
      } catch {
        // Non-blocking: keep user's typed name. Lookup failures are silent.
      }
    }
  };

  const updatePhaseTwo = async (idx, patch, { lookupPriceFor, regenerateConceptFor, regenerateImplFor } = {}) => {
    setData((prev) => {
      const projs = [...(prev.assessment.phase_two_projects || [])];
      projs[idx] = { ...projs[idx], ...patch };
      const next = { ...prev.assessment, phase_two_projects: projs };
      queueSave(next);
      return { ...prev, assessment: next };
    });
    if (regenerateConceptFor || regenerateImplFor) {
      try {
        const r = await api.post(`/interviews/${id}/regenerate-phase2`, {
          proj_idx: idx,
          new_name: regenerateConceptFor || null,
          new_implementation: regenerateImplFor || null,
        });
        const fresh = r.data || {};
        setData((prev) => {
          const projs = [...(prev.assessment.phase_two_projects || [])];
          projs[idx] = {
            ...projs[idx],
            name: fresh.name ?? projs[idx].name,
            description: fresh.description ?? projs[idx].description,
            complexity: fresh.complexity ?? projs[idx].complexity,
            impact: fresh.impact ?? projs[idx].impact,
            implementation: fresh.implementation ?? projs[idx].implementation,
            est_monthly_cost: fresh.est_monthly_cost ?? projs[idx].est_monthly_cost,
          };
          const next = { ...prev.assessment, phase_two_projects: projs };
          queueSave(next);
          return { ...prev, assessment: next };
        });
      } catch {
        // Silent
      }
    } else if (lookupPriceFor) {
      try {
        const r = await api.get(`/catalog/lookup?name=${encodeURIComponent(lookupPriceFor)}`);
        if (r.data?.price > 0) {
          setData((prev) => {
            const projs = [...(prev.assessment.phase_two_projects || [])];
            projs[idx] = { ...projs[idx], est_monthly_cost: r.data.price };
            const next = { ...prev.assessment, phase_two_projects: projs };
            queueSave(next);
            return { ...prev, assessment: next };
          });
        }
      } catch {
        // Non-blocking: lookup failures are silent.
      }
    }
  };

  const regen = async () => {
    setRegenerating(true);
    try {
      await api.post(`/interviews/${id}/analyze`);
      // Poll the status endpoint instead of waiting on the synchronous response —
      // the K8s ingress drops connections at ~60s, so we now run the LLM in a background task.
      let nextAssessment = null;
      const MAX_POLLS = 90; // 2s * 90 = 3 min ceiling
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const s = await api.get(`/interviews/${id}/analyze/status`);
          if (s.data.status === "ready") {
            nextAssessment = s.data.assessment;
            break;
          }
          if (s.data.status === "failed") {
            throw new Error(s.data.error || "Regeneration failed");
          }
        } catch (pollErr) {
          if (pollErr?.message && pollErr.message !== "Regeneration failed") {
            // transient — keep polling
          } else if (pollErr?.message === "Regeneration failed") {
            throw pollErr;
          }
        }
      }
      if (!nextAssessment) throw new Error("Regeneration timed out — please try again.");
      // Functional update — preserves any state changes made during the ~60s regen
      // (e.g., consultant_notes typed while the LLM was running). Using {...data, ...}
      // here would stale-close over `data` from before regen started and silently wipe
      // in-flight edits.
      setData((prev) => ({ ...(prev || {}), assessment: nextAssessment }));
      toast.success("Assessment regenerated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Failed");
    } finally { setRegenerating(false); }
  };

  const download = () => {
    const token = localStorage.getItem("gl_token");
    window.location.href = `${API}/interviews/${id}/pptx?token=${token}`;
  };

  if (!data) return <Layout><div className="p-12">Loading...</div></Layout>;

  const a = data.assessment;
  const hasAssessment = !!a;
  const isAiAssessmentIntake = (data.intake_type || "full_audit") === "ai_assessment";
  const showDocumentsPanel = !isAiAssessmentIntake; // Documents Panel is full-audit-only

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-6" data-testid="assessment-page">
        <Link to={`/interviews/${id}`} className="inline-flex items-center text-sm text-navy/60 hover:text-gold">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Interview
        </Link>

        <div className="bg-navy text-ivory p-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow text-gold">
              {hasAssessment ? "Generated Assessment" : isAiAssessmentIntake ? "AI Assessment" : "Clarity Workspace"}
            </div>
            <h1 className="font-display text-4xl mt-2">{data.business_name || data.client_name}</h1>
            <p className="text-ivory/60 mt-1">{data.client_name} · {data.industry}</p>
          </div>
          <div className="flex gap-2">
            {hasAssessment && (
              <>
                <Button onClick={saveAsSnapshot} variant="outline" data-testid="save-snapshot-btn"
                  className="rounded-none border-ivory/30 text-ivory hover:bg-ivory/10 hover:text-ivory">
                  <Library className="w-4 h-4 mr-2" /> Save as Snapshot
                </Button>
                <Button onClick={regen} disabled={regenerating} variant="outline" data-testid="regen-btn" className="rounded-none border-ivory/30 text-ivory hover:bg-ivory/10 hover:text-ivory">
                  {regenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />} Regenerate
                </Button>
                <Button onClick={download} data-testid="download-pptx-btn" className="bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold">
                  <Download className="w-4 h-4 mr-2" /> Download PowerPoint
                </Button>
              </>
            )}
            {!hasAssessment && (
              <Button onClick={() => nav(`/interviews/${id}`)} variant="outline"
                data-testid="back-to-interview-btn"
                className="rounded-none border-ivory/30 text-ivory hover:bg-ivory/10 hover:text-ivory">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Interview
              </Button>
            )}
          </div>
        </div>

        {/* Internal Notes — consultant-only, never shown to client */}
        <div className="bg-white border border-navy/10" data-testid="internal-notes-panel">
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-navy/10">
            <div className="flex items-center gap-3">
              <Lock className="w-4 h-4 text-gold" />
              <div>
                <div className="eyebrow text-gold">Internal Notes</div>
                <p className="text-xs text-navy/50 mt-0.5">Consultant-only. Never visible to the client. Autosaves.</p>
              </div>
            </div>
            {notesSaving && (
              <span className="text-xs text-gold flex items-center"><Save className="w-3 h-3 mr-1" /> Saving...</span>
            )}
          </div>
          <div className="p-4">
            <RichTextEditor
              value={data.consultant_notes || ""}
              onChange={updateNotes}
              placeholder="Pre-call talking points, client objections to address, follow-up reminders, custom pricing thoughts..."
              minHeight={200}
            />
          </div>
        </div>

        {/* Executive Summary */}
        {hasAssessment ? (
        <>
        <Card title="Executive Summary" eyebrow="Slide 2">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="eyebrow text-gold mb-2">Pain</div>
              <p className="text-navy">{a.executive_summary?.pain}</p>
            </div>
            <div>
              <div className="eyebrow text-gold mb-2">Outcome</div>
              <p className="text-navy">{a.executive_summary?.outcome}</p>
            </div>
            <div className="bg-navy text-ivory p-6 text-center">
              <div className="font-display text-6xl text-gold font-bold">{a.executive_summary?.hours_reclaimed_weekly}</div>
              <div className="text-xs tracking-widest mt-1">HOURS / WEEK RECLAIMED</div>
              <div className="text-xs text-ivory/70 mt-3">{a.executive_summary?.primary_focus}</div>
            </div>
          </div>
        </Card>

        {/* Current stack gaps */}
        <Card title="Current Stack Gaps" eyebrow="Slide 3">
          <div className="divide-y divide-navy/10">
            {(a.current_stack_gaps || []).map((g, i) => (
              <div key={i} className="grid md:grid-cols-3 gap-4 py-4">
                <div className="font-semibold text-navy">{g.category}</div>
                <div className="text-navy/80">{g.current || "—"}</div>
                <div className="text-navy/60 italic text-sm">{g.gap}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Impact-Effort Matrix */}
        <Card title="Impact–Effort Matrix" eyebrow="Slide 4">
          <div className="grid grid-cols-2 gap-3">
            {[
              ["High Impact · Low Effort", a.impact_effort_matrix?.high_impact_low_effort, "border-gold"],
              ["High Impact · High Effort", a.impact_effort_matrix?.high_impact_high_effort, "border-navy"],
              ["Low Impact · Low Effort", a.impact_effort_matrix?.low_impact_low_effort, "border-navy/30"],
              ["Low Impact · High Effort", a.impact_effort_matrix?.low_impact_high_effort, "border-navy/20"],
            ].map(([label, items, cls]) => (
              <div key={label} className={`border-2 ${cls} p-5 bg-ivory`}>
                <div className="eyebrow text-navy/70 mb-3">{label}</div>
                <ul className="space-y-1">
                  {(items || []).map((it, i) => <li key={i} className="text-sm text-navy">• {it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Card>

        {/* Recommended solutions */}
        <Card title="Recommended Solutions" eyebrow="Slide 5">
          <div className="-mt-4 mb-6 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-navy/60">
              <Pencil className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
              Click the tool name to swap. Use the checkbox to pick the {PPT_REC_LIMIT} that ship in the deck. ROI recalculates live from your selection.
            </p>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-navy text-ivory text-xs tracking-wider"
              data-testid="ppt-selected-counter"
            >
              <Check className="w-3.5 h-3.5 text-gold" />
              <span>
                {(a.recommended_solutions || []).filter((r) => r.selected_for_ppt !== false).length}
                <span className="text-ivory/60"> of {PPT_REC_LIMIT}</span> selected for PPT
              </span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(a.recommended_solutions || []).map((r, i) => (
              <RecSolutionCard
                key={i}
                idx={i}
                r={r}
                onChange={updateRecSolution}
                onToggleSelect={() => toggleRecSelection(i)}
              />
            ))}
          </div>
        </Card>

        {/* Financial */}
        <Card title="Financial Impact" eyebrow="Slide 6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-navy text-ivory p-8 text-center">
              <div className="font-display text-7xl text-gold font-bold">{a.financial_impact?.hours_returned_weekly}</div>
              <div className="text-xs tracking-widest mt-2">HOURS RETURNED WEEKLY</div>
            </div>
            <div className="space-y-3">
              {[
                ["Hourly value", `$${(a.financial_impact?.hourly_value || 0).toLocaleString()}`],
                ["Weekly value reclaimed", `$${(a.financial_impact?.weekly_value || 0).toLocaleString()}`],
                ["Monthly value reclaimed", `$${(a.financial_impact?.monthly_value || 0).toLocaleString()}`],
                ["Monthly tool cost", `$${(a.financial_impact?.monthly_tool_cost || 0).toLocaleString()}`],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between py-3 border-b border-navy/10">
                  <span className="text-navy/70">{l}</span>
                  <span className="font-display text-xl text-navy">{v}</span>
                </div>
              ))}
              <div className="bg-gold p-4 flex justify-between items-center mt-4">
                <span className="eyebrow text-navy">Monthly Net ROI</span>
                <span className="font-display text-3xl text-navy font-bold">${(a.financial_impact?.monthly_net_roi || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Wins */}
        <Card title="4-Day Quick Wins" eyebrow="Slide 7">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(a.four_day_quick_wins || []).map((d, i) => (
              <div key={i} className="bg-ivory border border-navy/10 p-5">
                <div className="bg-navy text-gold text-center py-1 text-xs tracking-widest -m-5 mb-4">DAY {d.day}</div>
                <div className="font-display text-lg text-navy mt-1">{d.tool}</div>
                <p className="text-sm text-navy/70 mt-2">{d.action}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Phase 2 */}
        <Card title="Phase 2 — Managed Implementation" eyebrow="Slide 8">
          <p className="text-sm text-navy/60 -mt-4 mb-6">
            <Pencil className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
            Each project has swappable concept + implementation options. PPT will show only your final selections.
          </p>
          <div className="space-y-4">
            {(a.phase_two_projects || []).map((p, i) => (
              <PhaseTwoCard key={i} idx={i} p={p} onChange={updatePhaseTwo} />
            ))}
          </div>
        </Card>

        {/* Engagement */}
        <Card title="Engagement Options" eyebrow="Slide 9">
          <p className="text-sm text-navy/60 -mt-4 mb-6">
            <Pencil className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
            Click any price to override. Changes save automatically and appear in the downloaded PowerPoint.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              ["Assessment Only", "assessment_only", a.engagement_options?.assessment_only, false],
              ["Guided Setup", "guided_setup", a.engagement_options?.guided_setup, true],
              ["Done-for-You", "done_for_you", a.engagement_options?.done_for_you, false],
            ].map(([fallback, key, tier, rec]) => {
              const name = (tier?.label || "").trim() || fallback;
              return (
              <div key={key} className={`p-6 ${rec ? "bg-navy text-ivory border-2 border-gold" : "bg-ivory border border-navy/10"}`}>
                {rec && <div className="eyebrow text-gold mb-2">★ Recommended</div>}
                <h4 className={`eyebrow ${rec ? "text-gold" : "text-navy"}`} data-testid={`tier-label-${key}`}>{name}</h4>
                <div className={`mt-3 flex items-baseline gap-1 font-display text-4xl font-bold ${rec ? "text-ivory" : "text-navy"}`}>
                  <span>$</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={tier?.price ?? 0}
                    onChange={(e) => updateTierPrice(key, e.target.value)}
                    data-testid={`tier-price-${key}`}
                    className={`bg-transparent w-full outline-none font-display font-bold tabular-nums focus:ring-2 focus:ring-gold/60 focus:ring-offset-2 ${rec ? "focus:ring-offset-navy text-ivory" : "focus:ring-offset-ivory text-navy"} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none hover:bg-black/5 rounded px-1 -mx-1`}
                    aria-label={`${name} price`}
                  />
                </div>
                <ul className={`mt-4 space-y-2 text-sm ${rec ? "text-ivory/80" : "text-navy/70"}`}>
                  {(tier?.included || []).map((it, i) => <li key={i}>✓ {it}</li>)}
                </ul>
              </div>
              );
            })}
          </div>
          {saving && (
            <div className="mt-4 text-xs text-gold flex items-center">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving price changes...
            </div>
          )}
        </Card>
        </>
        ) : (
          <div
            className="bg-white border border-navy/10 p-10 text-center"
            data-testid="no-assessment-banner"
          >
            <div className="eyebrow text-gold">No AI Assessment yet</div>
            <h2 className="font-display text-2xl text-navy mt-2">
              Run the AI analysis from the interview page
            </h2>
            <p className="text-navy/60 text-sm mt-3 max-w-md mx-auto">
              The PowerPoint slides, ROI tables, and engagement pricing all come from the AI assessment.
              {showDocumentsPanel ? " You can still generate the document packet below using the intake and transcript data you've captured." : ""}
            </p>
            <Button
              onClick={() => nav(`/interviews/${id}`)}
              data-testid="goto-interview-btn"
              className="mt-6 bg-navy text-ivory rounded-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Interview
            </Button>
          </div>
        )}

        {showDocumentsPanel && <DocumentsPanel interviewId={id} interview={data} />}

        {hasAssessment && (
        <div className="flex justify-center pt-4 pb-8">
          <Button onClick={download} data-testid="download-pptx-bottom" className="bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold h-12 px-8">
            <Download className="w-5 h-5 mr-2" /> Download Branded PowerPoint
          </Button>
        </div>
        )}
      </div>
    </Layout>
  );
}

function Card({ title, eyebrow, children }) {
  return (
    <div className="bg-white border border-navy/10 p-8">
      <div className="flex items-baseline gap-3 mb-6">
        <div className="eyebrow text-gold">{eyebrow}</div>
        <h2 className="font-display text-2xl text-navy">{title}</h2>
      </div>
      {children}
    </div>
  );
}

const CUSTOM = "__custom__";

function PickerWithCustom({ value, options, onChange, placeholder, testid }) {
  const [customMode, setCustomMode] = useState(false);
  const [draft, setDraft] = useState("");
  const allOptions = [...new Set([value, ...(options || [])].filter(Boolean))];
  const showPicker = allOptions.length > 1 || (options || []).length > 0;

  if (customMode) {
    return (
      <div className="flex gap-1 items-center">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => { if (draft.trim()) { onChange(draft.trim()); } setCustomMode(false); setDraft(""); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.target.blur(); }
            if (e.key === "Escape") { setCustomMode(false); setDraft(""); }
          }}
          placeholder={placeholder || "Type a tool name..."}
          data-testid={testid ? `${testid}-custom-input` : undefined}
          className="flex-1 bg-white border border-gold rounded-none px-2 py-1 font-display text-lg text-navy outline-none"
        />
      </div>
    );
  }

  if (!showPicker) {
    return (
      <div className="flex items-center gap-2">
        <span className="font-display text-xl text-navy">{value}</span>
        <button onClick={() => { setDraft(value || ""); setCustomMode(true); }} className="text-[10px] text-gold hover:underline uppercase tracking-widest" data-testid={testid ? `${testid}-edit` : undefined}>edit</button>
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === CUSTOM) { setDraft(""); setCustomMode(true); return; }
        onChange(v);
      }}
      data-testid={testid}
      className="font-display text-xl text-navy bg-transparent border-none outline-none cursor-pointer hover:bg-gold/10 rounded px-1 -mx-1 focus:ring-2 focus:ring-gold/60 appearance-none pr-6"
      style={{ backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23B8955A%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22/%3E%3C/svg%3E')", backgroundRepeat: "no-repeat", backgroundPosition: "right .5rem center", backgroundSize: ".65em auto" }}
    >
      {allOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      <option value={CUSTOM}>+ Custom…</option>
    </select>
  );
}

function EditablePrice({ value, onChange, testid, className = "" }) {
  return (
    <div className="inline-flex items-baseline">
      <span className="text-navy/60">$</span>
      <input
        type="number"
        min="0"
        step="1"
        value={value ?? 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        data-testid={testid}
        className={`bg-transparent outline-none font-display tabular-nums focus:ring-2 focus:ring-gold/60 hover:bg-black/5 rounded px-1 -mx-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${className}`}
        style={{ width: `${Math.max(3, String(Math.round(value || 0)).length + 1)}ch` }}
        aria-label="Price"
      />
      <span className="text-navy/60">/mo</span>
    </div>
  );
}

function RecSolutionCard({ idx, r, onChange, onToggleSelect }) {
  const [regenerating, setRegenerating] = useState(false);
  // Always include the current tool_name first so swapping back is always possible
  const toolOptions = [...new Set([r.tool_name, ...(r.alternatives || [])].filter(Boolean))];
  const isSelected = r.selected_for_ppt !== false;

  const pickTool = async (name) => {
    if (!name || name === r.tool_name) return;
    // Preserve the previous tool inside alternatives so the user can swap back
    const prev = r.tool_name;
    const nextAlts = [...new Set([prev, ...(r.alternatives || [])].filter((x) => x && x !== name))];
    setRegenerating(true);
    try {
      await onChange(idx, { tool_name: name, alternatives: nextAlts }, { regenerateFor: name });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className={`border bg-ivory p-6 relative transition ${isSelected ? "border-gold" : "border-navy/15 opacity-60"}`}>
      {regenerating && (
        <div className="absolute inset-0 bg-ivory/80 flex items-center justify-center z-10" data-testid={`rec-regen-overlay-${idx}`}>
          <div className="text-xs text-gold flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Regenerating for {r.tool_name}…
          </div>
        </div>
      )}
      <div className="flex items-start justify-between mb-2 gap-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            data-testid={`rec-select-${idx}`}
            aria-label={isSelected ? "Selected for PowerPoint" : "Click to include in PowerPoint"}
            title={isSelected ? "Selected for PowerPoint — uncheck to exclude" : "Click to include in PowerPoint"}
            className="w-4 h-4 accent-gold cursor-pointer"
          />
          <span className="font-display text-gold text-lg">0{idx + 1}</span>
        </div>
        <EditablePrice
          value={r.monthly_cost}
          onChange={(v) => onChange(idx, { monthly_cost: v })}
          testid={`rec-price-${idx}`}
          className="text-sm text-navy/70"
        />
      </div>
      <PickerWithCustom
        value={r.tool_name}
        options={toolOptions}
        onChange={pickTool}
        testid={`rec-tool-${idx}`}
      />
      <p className="text-xs text-gold uppercase tracking-wider mt-2">{r.pain_point}</p>
      <p className="text-sm text-navy/80 mt-3">{r.why_it_fits}</p>
      <div className="mt-4 text-xs text-navy/60">
        Setup: {r.setup_minutes}min · Saves {r.hours_saved_weekly} hrs/wk
      </div>
      {!isSelected && (
        <div className="mt-3 text-[10px] uppercase tracking-widest text-navy/40 font-semibold">
          ✕ Not in PowerPoint
        </div>
      )}
      {r.build_in_house?.recommended && (
        <div className="mt-3 border-t border-navy/10 pt-3 flex items-start gap-2 text-xs">
          <Check className="w-4 h-4 text-gold flex-shrink-0" />
          <span className="text-navy/80"><strong>Goldline could build this:</strong> {r.build_in_house.reason}</span>
        </div>
      )}
      {r.build_in_house && !r.build_in_house.recommended && (
        <div className="mt-3 border-t border-navy/10 pt-3 flex items-start gap-2 text-xs">
          <X className="w-4 h-4 text-navy/40 flex-shrink-0" />
          <span className="text-navy/60">{r.build_in_house.reason}</span>
        </div>
      )}
    </div>
  );
}

function PhaseTwoCard({ idx, p, onChange }) {
  const [regenerating, setRegenerating] = useState(false);

  // Always include the current concept first so swapping back is possible
  const conceptOptions = [
    { name: p.name, description: p.description, complexity: p.complexity, impact: p.impact },
    ...(p.concept_options || []),
  ].filter((c) => c?.name);
  // Dedupe by name
  const conceptByName = new Map();
  conceptOptions.forEach((c) => { if (!conceptByName.has(c.name)) conceptByName.set(c.name, c); });
  const conceptNames = Array.from(conceptByName.keys());

  // Always include the current implementation first
  const implOptions = [
    ...(p.implementation ? [{ name: p.implementation, monthly_cost: p.est_monthly_cost }] : []),
    ...(p.implementation_options || []),
  ].filter((o) => o?.name);
  const implByName = new Map();
  implOptions.forEach((o) => { if (!implByName.has(o.name)) implByName.set(o.name, o); });
  const implNames = Array.from(implByName.keys());

  const pickConcept = async (conceptName) => {
    if (!conceptName || conceptName === p.name) return;
    // Preserve the previous concept inside concept_options so the user can swap back
    const prevConcept = { name: p.name, description: p.description, complexity: p.complexity, impact: p.impact };
    const nextOptions = [
      prevConcept,
      ...(p.concept_options || []).filter((c) => c?.name && c.name !== conceptName),
    ];
    setRegenerating(true);
    try {
      await onChange(idx, { name: conceptName, concept_options: nextOptions }, { regenerateConceptFor: conceptName });
    } finally {
      setRegenerating(false);
    }
  };

  const pickImpl = async (implName) => {
    if (!implName || implName === p.implementation) return;
    // Preserve the previous implementation inside implementation_options
    const prevImpl = { name: p.implementation, monthly_cost: p.est_monthly_cost };
    const nextOptions = [
      ...(p.implementation ? [prevImpl] : []),
      ...(p.implementation_options || []).filter((o) => o?.name && o.name !== implName),
    ];
    setRegenerating(true);
    try {
      await onChange(idx, { implementation: implName, implementation_options: nextOptions }, { regenerateImplFor: implName });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row items-start gap-5 p-5 bg-ivory border border-navy/10 relative">
      {regenerating && (
        <div className="absolute inset-0 bg-ivory/80 flex items-center justify-center z-10" data-testid={`phase2-regen-overlay-${idx}`}>
          <div className="text-xs text-gold flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Regenerating Phase 2 card…
          </div>
        </div>
      )}
      <div className="font-display text-4xl text-gold">0{idx + 1}</div>
      <div className="flex-1 w-full space-y-3">
        <div>
          <div className="eyebrow text-navy/50 mb-1">Concept</div>
          <PickerWithCustom
            value={p.name}
            options={conceptNames}
            onChange={pickConcept}
            testid={`phase2-concept-${idx}`}
          />
        </div>
        <p className="text-sm text-navy/70">{p.description}</p>
        <div>
          <div className="eyebrow text-navy/50 mb-1">Implementation</div>
          <PickerWithCustom
            value={p.implementation || implNames[0] || ""}
            options={implNames}
            onChange={pickImpl}
            testid={`phase2-impl-${idx}`}
          />
        </div>
      </div>
      <div className="text-right space-y-1 text-xs min-w-[140px]">
        <div className="font-display text-lg text-navy">
          <EditablePrice
            value={p.est_monthly_cost}
            onChange={(v) => onChange(idx, { est_monthly_cost: v })}
            testid={`phase2-price-${idx}`}
            className="text-lg text-navy"
          />
        </div>
        <div className="text-navy/60">Complexity: {p.complexity}</div>
        <div className="text-gold font-semibold">{p.impact}</div>
      </div>
    </div>
  );
}
